import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { inject } from '@angular/core';
import { createAuthGuard } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';
import { AuthorizationRequirement } from '../models/authorization-requirement';
import { User, UserRole } from '../models/user';
import { NimmstaCommonsModuleConfiguration } from '../nimmsta-commons-module.config';
import { NavItem } from '../components/menu/navItem';
import { AuthorizationService } from '../services/authorization.service';
import { AuthService } from '../services/auth.service';
import { NAV_ITEM_SERVICE, NavItemService } from '../services/navitem.service.interface';
import Keycloak from 'keycloak-js';

const isAuthenticated = async (
  _: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<boolean | UrlTree> => {
  const keycloak = inject(Keycloak);
  if (keycloak === null || !keycloak.didInitialize) {
    console.error(
      '%c[AUTH ERROR] Keycloak unavailable or initialization failed. Please check your authentication setup.',
      'color: white; background: red; font-size: 16px; font-weight: bold; padding: 4px;',
    );
    const router = inject(Router);
    return router.parseUrl('/500');
  }

  if (keycloak.authenticated) {
    return true;
  }

  await keycloak.login({
    redirectUri: window.location.origin + state.url,
    prompt: 'login',
  });

  // If the call to login fails, redirect to the error page
  console.error(
    '%c[AUTH ERROR] Keycloak login failed. Please check your authentication setup.',
    'color: white; background: red; font-size: 16px; font-weight: bold; padding: 4px;',
  );
  const router = inject(Router);
  return router.parseUrl('/500');
};

const isAdmin = async (): Promise<boolean | UrlTree> => {
  const keycloak = inject(Keycloak);
  const config = inject(NimmstaCommonsModuleConfiguration);
  if (keycloak === null || config === null) {
    return false;
  }

  if (!keycloak.authenticated) {
    return false;
  }

  const roles =
    keycloak.tokenParsed!.resource_access?.[config.keycloakClientId]?.roles;
  if (roles) {
    if (
      roles.some((role) => role.toLowerCase() === UserRole.ADMIN.toLowerCase())
    ) {
      return true;
    } else {
      if (config.applicationUrls.workflow) {
        window.open(config.applicationUrls.workflow, '_self');
      }
      return false;
    }
  }

  return false;
};

const isAuthorized = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const authorizationService = inject(AuthorizationService);
  const navService = inject(NAV_ITEM_SERVICE);
  const router = inject(Router);
  const authenticated = await isAuthenticated(route, state);
  if (authenticated !== true) {
    return authenticated;
  }

  const user = await firstValueFrom(authService.ensureCurrentUser());
  const requirement = route.data['auth'] as AuthorizationRequirement | undefined;
  if (!requirement) {
    return true;
  }

  const hasAccess = authorizationService.matches(requirement, user);

  if (hasAccess) {
    return true;
  }

  if (route.data['redirectToFirstVisibleNav']) {
    const firstVisibleNavUrl = await getFirstVisibleSidebarUrl(user, navService);
    if (firstVisibleNavUrl && firstVisibleNavUrl !== state.url) {
      return router.parseUrl(firstVisibleNavUrl);
    }
  }

  return router.parseUrl('/dashboard');
};

const getFirstVisibleSidebarUrl = async (
  user: User,
  navService: NavItemService,
): Promise<string | null> => {
  const navItems = await firstValueFrom(navService.getSidebarNavItems());
  return findFirstVisibleNavUrl(navItems, user);
};

const findFirstVisibleNavUrl = (
  navItems: NavItem[],
  user: User,
): string | null => {
  for (const navItem of navItems) {
    if (!navItem.isVisible(user)) {
      continue;
    }

    if (navItem.url) {
      return `/${navItem.url}`;
    }

    if (navItem.items?.length) {
      const childUrl = findFirstVisibleNavUrl(navItem.items, user);
      if (childUrl) {
        return childUrl;
      }
    }
  }

  return null;
};

export const isAuthenticatedGuard =
  createAuthGuard<CanActivateFn>(isAuthenticated);
export const isAdminGuard = createAuthGuard<CanActivateFn>(isAdmin);
export const hasAuthorizationGuard = createAuthGuard<CanActivateFn>(isAuthorized);
